<template>
  <BaseModal :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)">
    <div class="head">
      <h2 class="title">Script d'appel</h2>
      <select v-if="scripts.length > 1" v-model="selectedIdx" class="picker">
        <option v-for="(s, i) in scripts" :key="i" :value="i">{{ s.name }} · {{ sourceLabel(s.source) }}</option>
      </select>
    </div>

    <div v-if="loading" class="state">Chargement…</div>
    <div v-else-if="scripts.length === 0" class="state">
      Aucun script. <button class="link" @click="newPersonal">Créer un script personnel</button>
    </div>

    <template v-else-if="current">
      <!-- Mode édition (script personnel) -->
      <div v-if="editing" class="edit">
        <input v-model="editName" class="edit-name" placeholder="Nom du script" />
        <textarea v-model="editContent" class="edit-content" placeholder="Variables : {{name}}, {{phone}}…" />
        <div class="edit-actions">
          <button class="btn-secondary" @click="editing = false">Annuler</button>
          <button class="btn-primary" :disabled="busy || !editName.trim()" @click="saveEdit">Enregistrer</button>
        </div>
      </div>

      <!-- Mode lecture -->
      <template v-else>
        <pre class="content">{{ resolved }}</pre>
        <div class="actions">
          <span class="src">{{ sourceLabel(current.source) }}</span>
          <span class="spacer" />
          <button v-if="current.source === 'personal' && current.id" class="btn-secondary" :disabled="busy" @click="startEdit">Éditer</button>
          <button v-if="current.id" class="btn-secondary" :disabled="busy" @click="duplicate">Dupliquer & personnaliser</button>
          <button class="btn-secondary" @click="newPersonal" :disabled="busy">Nouveau perso</button>
          <button class="btn-secondary" @click="$emit('update:modelValue', false)">Fermer</button>
        </div>
      </template>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { QueueAPI, type QueueEntry } from '@/shared/api/queue'
import { ScriptAPI, type CallScript } from '@/shared/api/forms'
import { useToasts } from '@/shared/stores/toasts'
import { useAuthStore } from '@/shared/stores/auth'
import { substituteScriptVariables } from '@/shared/utils/script_variables'
import BaseModal from '@/shared/components/BaseModal.vue'

const props = defineProps<{ modelValue: boolean; entry: QueueEntry | null }>()
defineEmits<{ (e: 'update:modelValue', v: boolean): void }>()

const toasts = useToasts()
const auth = useAuthStore()
const loading = ref(false)
const busy = ref(false)
const scripts = ref<CallScript[]>([])
const selectedIdx = ref(0)
const editing = ref(false)
const editName = ref('')
const editContent = ref('')

const current = computed<CallScript | null>(() => scripts.value[selectedIdx.value] ?? null)

function sourceLabel(s: string): string {
  return { campaign: 'Campagne', assigned: 'Assigné', personal: 'Personnel' }[s] || s
}

/** Substitue les variables ({{name}}, {{phone}}, {{agent_name}}, {{date}},
 *  champs personnalisés…) via le catalogue partagé. */
const resolved = computed(() =>
  substituteScriptVariables(current.value?.content || '', {
    entry: props.entry,
    agent: {
      fullName: auth.displayName,
      firstName: auth.user?.first_name,
      teamName: auth.user?.team_name,
    },
  }),
)

async function load(selectId?: number) {
  if (!props.entry) { scripts.value = []; return }
  loading.value = true
  editing.value = false
  try {
    const r = await QueueAPI.scripts(props.entry.id)
    scripts.value = r.scripts || []
    if (selectId != null) {
      const i = scripts.value.findIndex((s) => s.id === selectId)
      selectedIdx.value = i >= 0 ? i : 0
    } else {
      selectedIdx.value = 0
    }
  } catch {
    scripts.value = []
  } finally { loading.value = false }
}

function startEdit() {
  if (!current.value) return
  editName.value = current.value.name
  editContent.value = current.value.content
  editing.value = true
}
async function saveEdit() {
  if (!current.value?.id) return
  busy.value = true
  try {
    await ScriptAPI.update(current.value.id, { name: editName.value, content: editContent.value })
    toasts.success('Script mis à jour')
    editing.value = false
    await load(current.value.id)
  } catch (e: any) {
    toasts.error('Erreur', e?.response?.data?.detail || 'Enregistrement impossible.')
  } finally { busy.value = false }
}
async function duplicate() {
  if (!current.value?.id) return
  busy.value = true
  try {
    const copy = await ScriptAPI.duplicate(current.value.id)
    toasts.success('Réplique personnelle créée')
    await load(copy.id)
    startEdit()
  } catch (e: any) {
    toasts.error('Erreur', e?.response?.data?.detail || 'Duplication impossible.')
  } finally { busy.value = false }
}
async function newPersonal() {
  busy.value = true
  try {
    const created = await ScriptAPI.create({ name: 'Mon script', content: current.value?.content || '' })
    await load(created.id)
    startEdit()
  } catch (e: any) {
    toasts.error('Erreur', e?.response?.data?.detail || 'Création impossible.')
  } finally { busy.value = false }
}

watch(() => props.modelValue, (open) => { if (open) load() })
</script>

<style scoped>
.head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.title { font-size: 16px; font-weight: 700; margin: 0; }
.picker { padding: 6px 10px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); font-size: 12.5px; max-width: 260px; }
.state { padding: 20px; text-align: center; color: var(--text-muted); }
.link { background: none; border: none; color: var(--primary); cursor: pointer; font: inherit; text-decoration: underline; }
.content {
  white-space: pre-wrap; word-break: break-word;
  font-family: var(--font, inherit); font-size: 14px; line-height: 1.65; color: var(--text);
  background: var(--surface-hover); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 16px; max-height: 52vh; overflow-y: auto; margin: 0; min-width: 440px; max-width: 580px;
}
.actions { display: flex; align-items: center; gap: 8px; margin-top: 14px; }
.src { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }
.spacer { flex: 1; }
.edit { display: flex; flex-direction: column; gap: 10px; min-width: 440px; }
.edit-name { padding: 9px 11px; border: 1px solid var(--border); border-radius: var(--radius); font-size: 14px; font-weight: 600; }
.edit-content { min-height: 220px; padding: 12px; border: 1px solid var(--border); border-radius: var(--radius); font-size: 14px; line-height: 1.6; font-family: var(--font, inherit); resize: vertical; }
.edit-actions { display: flex; justify-content: flex-end; gap: 8px; }
.btn-primary { padding: 8px 14px; background: var(--primary); color: #fff; border: none; border-radius: var(--radius); font-weight: 600; font-size: 13px; cursor: pointer; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary { padding: 8px 12px; background: var(--surface-hover); border: 1px solid var(--border); border-radius: var(--radius); font-weight: 500; font-size: 12.5px; cursor: pointer; }
</style>
